import { motion } from "framer-motion";
import { Check, X } from "lucide-react";

const ROWS = [
  { label: "Site Quality", others: "Templates & themes", ours: "Custom, conversion-led design" },
  { label: "SEO Strategy", others: "Basic SEO checklist", ours: "SEO + GEO + AI citations" },
  { label: "Support", others: "Slow ticket queues", ours: "Dedicated growth partner" },
  { label: "Strategy", others: "Generic playbook", ours: "Bespoke growth system" },
  { label: "Outcome", others: "Pretty design", ours: "Revenue & qualified pipeline" },
];

export default function WhyChoose() {
  return (
    <section data-testid="why-choose" className="section relative">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 text-xs font-semibold tracking-widest uppercase">
            Why EchoWings
          </div>
          <h2 className="mt-5 font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">
            The difference is in the details.
          </h2>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7 }}
        >
          {/* DESKTOP / TABLET TABLE (md+) */}
          <div className="hidden md:block rounded-3xl border border-white/10 overflow-hidden bg-[#0a0a0a]">
            <div className="grid grid-cols-3 text-xs md:text-sm uppercase tracking-widest font-semibold border-b border-white/10">
              <div className="px-6 py-5 text-white/40">Criteria</div>
              <div className="px-6 py-5 text-white/40 border-l border-white/10">
                Other Agencies
              </div>
              <div className="px-6 py-5 text-cyan-300 border-l border-white/10 bg-gradient-to-r from-blue-600/10 to-cyan-400/5">
                EchoWings Media
              </div>
            </div>
            {ROWS.map((r, i) => (
              <div
                key={r.label}
                className={`grid grid-cols-3 text-sm md:text-base ${
                  i !== ROWS.length - 1 ? "border-b border-white/5" : ""
                }`}
                data-testid={`compare-row-${i}`}
              >
                <div className="px-6 py-5 font-semibold text-white">
                  {r.label}
                </div>
                <div className="px-6 py-5 text-white/50 border-l border-white/10 flex items-start gap-3">
                  <span className="mt-0.5 w-5 h-5 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                    <X className="w-3 h-3 text-white/40" />
                  </span>
                  <span>{r.others}</span>
                </div>
                <div className="px-6 py-5 text-white border-l border-white/10 bg-gradient-to-r from-blue-600/[0.06] to-cyan-400/[0.03] flex items-start gap-3">
                  <span className="mt-0.5 w-5 h-5 rounded-full bg-gradient-to-br from-blue-600 to-cyan-400 flex items-center justify-center shrink-0">
                    <Check className="w-3 h-3 text-black" />
                  </span>
                  <span className="font-medium">{r.ours}</span>
                </div>
              </div>
            ))}
          </div>

          {/* MOBILE CARDS (< md) */}
          <div className="md:hidden space-y-4">
            {ROWS.map((r, i) => (
              <div
                key={r.label}
                data-testid={`compare-row-mobile-${i}`}
                className="rounded-2xl border border-white/10 bg-[#0a0a0a] overflow-hidden"
              >
                <div className="px-5 py-3 border-b border-white/10 text-[11px] uppercase tracking-widest text-cyan-300 font-semibold">
                  {r.label}
                </div>
                <div className="grid grid-cols-1 divide-y divide-white/5">
                  <div className="px-5 py-4 flex items-start gap-3">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                      <X className="w-3 h-3 text-white/40" />
                    </span>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-white/35 font-semibold mb-0.5">
                        Other Agencies
                      </div>
                      <div className="text-sm text-white/55">{r.others}</div>
                    </div>
                  </div>
                  <div className="px-5 py-4 flex items-start gap-3 bg-gradient-to-r from-blue-600/[0.06] to-cyan-400/[0.03]">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-gradient-to-br from-blue-600 to-cyan-400 flex items-center justify-center shrink-0">
                      <Check className="w-3 h-3 text-black" />
                    </span>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold mb-0.5">
                        EchoWings Media
                      </div>
                      <div className="text-sm text-white font-medium">{r.ours}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
